'use client';

import {
  Card,
  CopyBtn,
  Separator,
  Skeleton,
  StatusIcon,
} from '@mokoboko/components';
import { Calendar, CreditCard, Gift, Hourglass } from 'lucide-react';

import { GiftCard } from '@/components/gift-card';
import {
  giftCardValue,
  giftReceiptCreditValidity,
  giftReceiptDeliveryLine,
  giftReceiptStatusLine,
  giftReceiptValidUntil,
  receiptGiftCardValue,
} from '@/helpers/gift-card';
import { GIFT_CARD_ASPECT } from '@/lib/gift-card-design';
import type { GiftCardReceiptCode, Order } from '@/lib/types';
import { formatAmount } from '@/lib/utils';
import useTenant from '@/state/tenant';
import {
  ReceiptEmailRow,
  ReceiptRow,
  ReceiptRowSkeleton,
  ReceiptStatusHeader,
} from './receipt-rows';

const SoldCard = ({
  code,
  studioName,
}: {
  code: GiftCardReceiptCode;
  studioName: string;
}) => (
  // text-left: the status header centres its text, and the card's anatomy is
  // left-aligned on every surface.
  <div aria-hidden className="mt-5 w-60 text-left">
    <GiftCard
      studioName={studioName}
      look={code.look}
      brandColor={code.look.type === 'theme' ? code.look.brand_color : null}
      headline={giftCardValue(receiptGiftCardValue(code))}
      compact
    />
  </div>
);

// The sold card sits under the delivery line. What was paid is an icon row
// below, like every receipt's.
export const GiftCardOrderReceipt = ({ order }: { order: Order }) => {
  const { data: tenant } = useTenant();
  const code = order.gift_card_order_items?.[0]?.gift_card_code ?? null;
  const studioName = tenant?.name ?? '';
  const timeZone = tenant?.schedule_config?.timezone;
  const creditValidity = code && giftReceiptCreditValidity(code);
  // A partial refund keeps the order paid but stops the card (D68), and a
  // send-to-self card can be redeemed or spent before its send day (D103).
  const statusLine = code && giftReceiptStatusLine(code);
  const title = code?.name ?? 'Gift card';
  const paid = formatAmount(
    order.final_amount ?? order.total_amount,
    order.currency
  );

  if (order.payment_status === 'REFUNDED') {
    return (
      <div className="flex flex-col gap-5 sm:gap-6">
        <ReceiptStatusHeader status="payment" label="Refunded" title={title}>
          <p className="mt-2 text-sm text-gray-700">
            This order was refunded, and the gift card no longer works.
          </p>
        </ReceiptStatusHeader>
        <Separator />
        <ReceiptRow icon={CreditCard} strikethrough title={paid}>
          <p className="mt-1 text-sm text-gray-600">Refunded</p>
        </ReceiptRow>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 sm:gap-6">
      <ReceiptStatusHeader status="confirmed" title={title}>
        {code && (
          <>
            {/* ph-no-capture keeps the recipient's name, and the code below,
                out of session replays, which mask only inputs. */}
            <p className="ph-no-capture mt-2 text-sm text-gray-700">
              {statusLine ??
                giftReceiptDeliveryLine(code, timeZone, new Date())}
            </p>
            <SoldCard code={code} studioName={studioName} />
          </>
        )}
      </ReceiptStatusHeader>

      <Separator />

      <div className="flex flex-col gap-4">
        {code?.code && !statusLine && (
          <div className="ph-no-capture flex items-center gap-4">
            <StatusIcon variant="filled" size="xl" icon={Gift} />
            <div className="min-w-0">
              <p className="font-mono text-sm font-semibold tracking-wider text-gray-900">
                {code.code}
              </p>
              <p className="mt-1 text-sm text-gray-600">Your gift card code</p>
            </div>
            <CopyBtn
              text={code.code}
              variant="tertiary-gray"
              size="sm"
              ariaLabel="Copy the gift card code"
              className="ml-auto shrink-0"
            />
          </div>
        )}
        {code && !statusLine && (
          <ReceiptRow icon={Calendar} title={giftReceiptValidUntil(code)}>
            <p className="mt-1 text-sm text-gray-600">Valid until</p>
          </ReceiptRow>
        )}
        {creditValidity && !statusLine && (
          <ReceiptRow icon={Hourglass} title={creditValidity}>
            <p className="mt-1 text-sm text-gray-600">Classes last</p>
          </ReceiptRow>
        )}
        <ReceiptRow icon={CreditCard} title={paid}>
          <p className="mt-1 text-sm text-gray-600">Paid</p>
        </ReceiptRow>
        {!statusLine && order.user?.email && (
          <ReceiptEmailRow email={order.user.email} />
        )}
      </div>
    </div>
  );
};

export const GiftCardReceiptSkeleton = () => (
  <Card contentClassName="p-5 sm:p-6 flex flex-col gap-5 sm:gap-6">
    <div className="flex items-center justify-between gap-4">
      <Skeleton isRounded className="!h-11 w-[140px] sm:!h-10 sm:w-[124px]" />
    </div>

    <Separator />

    <div className="flex flex-col gap-5 sm:gap-6">
      <div className="flex flex-col items-center">
        <Skeleton isCircle height={80} />
        <div className="mt-3">
          <Skeleton text="body-sm" width={80} />
        </div>
        <div className="mt-1.5">
          <Skeleton text="h2" width={208} />
        </div>
        <div className="mt-2">
          <Skeleton text="body-sm" width={256} />
        </div>
        <div className="mt-5 w-60">
          <Skeleton
            isRounded
            className="!h-auto w-full"
            style={{ aspectRatio: GIFT_CARD_ASPECT }}
          />
        </div>
      </div>

      <Separator />

      <div className="flex flex-col gap-4">
        {Array.from({ length: 2 }).map((_, i) => (
          <ReceiptRowSkeleton key={i} />
        ))}
      </div>
    </div>
  </Card>
);
